import { Module } from 'vuex'
import { PTRootState } from '@pt/store/index.type'
import useIdToFindCategory from '@/views/creative/utils/use-id-to-find-category'
import _ from 'lodash'
import { language } from '@/common/storage'
import { getJsonModelPath } from '@/views/creative/utils'
import { isPresetPrefix } from '@/views/creative/utils/preset-utils'
import { getAllSkinTexturePrefixByCategory } from '@/views/creative/utils/model-material-utils'
import {
  OrderListItem,
  OrderListQueryConfig,
  SubmitOrderData,
  SubmitOrderExtData,
} from '@/api/demand/order.type'
import { getOrderList, submitOrder } from '@/api/demand/order'

export interface ModelOrderState {
  categoryId: number
  orderList: OrderListItem[]
}

const ModelOrder: Module<ModelOrderState, PTRootState> = {
  namespaced: true,
  state: {
    categoryId: 0,
    orderList: [],
  },
  mutations: {
    setCategoryId(state, categoryId: number) {
      state.categoryId = categoryId
    },
    setOrderList(state, orderList: OrderListItem[]) {
      state.orderList = orderList
    },
  },
  actions: {
    /**
     * 创建模型订单
     * @param commit
     * @param state
     * @param rootState
     * @param materials 模型文件列表
     * @param remark 备注
     */
    async createModelOrder(
      { commit, state, rootState },
      {
        materials,
        remark,
        usage_type,
      }: {
        materials: SubmitOrderData['materials']
        remark?: SubmitOrderData['remark']
        usage_type: SubmitOrderData['usage_type']
      },
    ) {
      const currentCategory = useIdToFindCategory({
        category: _.get(rootState, 'creative.template.templateConfig.category'),
        categoryId: state.categoryId,
      })
      const currentResource = _.get(rootState, [
        'creative',
        'template',
        'templateConfig',
        'resource',
        state.categoryId,
        0,
      ])
      if (!currentResource) {
        throw new Error('模板资源不存在')
      }
      // 模型转换需要的参数
      const extData: SubmitOrderExtData = {
        filePath: currentResource.real_file_path,
        mod: _.get(rootState, 'creative.template.isGeneralTemplateService')
          ? 'gm'
          : 'dm',
        model: getJsonModelPath(currentResource.file_path),
        tplId: _.get(rootState, 'creative.template.templateId'),
        preset: isPresetPrefix(currentCategory.prefix) ? 1 : 0,
        textures: getAllSkinTexturePrefixByCategory(currentCategory),
      }

      const postData: SubmitOrderData = {
        materials: JSON.stringify(materials),
        project_id: rootState.creative.template.id,
        category_id: state.categoryId,
        category_name: currentCategory.name,
        remark: remark,
        skeleton_type: _.get(currentResource, 'extra.skeleton_type'),
        lang: language.get(),
        ext_data: JSON.stringify(extData),
        usage_type: usage_type,
      }
      try {
        const res = await submitOrder(postData)
        console.log('createModelOrder', res)
        return res
      } catch (e) {
        console.error('createModelOrder', e)
      }
    },
    /**
     * 获取订单列表
     * @param commit
     * @param queryConfig 查询配置
     */
    async getOrderList({ commit }, queryConfig: OrderListQueryConfig) {
      try {
        const res = await getOrderList(queryConfig)
        commit('setOrderList', res.data.data.list)
        return res
      } catch (e) {
        console.error('getOrderList', e)
      }
    },
    /**
     * 校验是否可以转换为 Cocos 模型
     * @param dispatch
     * @param fileUrl 模型文件地址
     * @param extData 工单的扩展数据，需要先通过 JSON.parse 转换为对象
     */
    async tryConvertCocosModel(
      { dispatch },
      {
        fileUrl,
        extData,
      }: {
        fileUrl: string
        extData: SubmitOrderExtData
      },
    ): Promise<boolean> {
      try {
        const res = await dispatch(
          'creative/template/model/convertCocosModel',
          {
            file_url: fileUrl,
            mod: extData.mod,
            model: extData.model,
            tpl_id: extData.tplId,
            preset: extData.preset,
            textures: extData.textures,
          },
          { root: true },
        )
        if (res.data.code === 0) {
          return true
        }
        return false
      } catch (e) {
        console.error('tryConvertCocosModel', e)
        return false
      }
    },

    async convertCocosModelByQuery(
      { dispatch, rootState },
      queryConfig: SubmitOrderExtData,
    ) {
      console.log('convertCocosModelByQuery', queryConfig)
      try {
        const res = await dispatch(
          'creative/template/model/convertCocosModel',
          {
            file_url: queryConfig.fileUrl,
            mod: queryConfig.mod,
            model: queryConfig.model,
            tpl_id: Number(queryConfig.tplId),
            preset: Number(queryConfig.preset),
            textures: queryConfig.textures
              ? queryConfig.textures.split(',')
              : undefined,
          },
          { root: true },
        )
        if (res.data.code === 0) {
          const modelFiles = res.data.data.files
          const textures = res.data.data.textures
          const allResources = _.get(
            rootState,
            'creative.template.templateConfig.resource',
            {},
          )
          const targetModelResource = _.find(
            _.flatMap(allResources, (value) => value),
            (resource) => {
              return resource.real_file_path === queryConfig.filePath
            },
          )
          if (targetModelResource) {
            await dispatch(
              'creative/template/updateResource',
              {
                ...targetModelResource,
                preview_urls: modelFiles,
                res_id: String(queryConfig.preview_asset_id),
                res_source: 'assets',
              },
              {
                root: true,
              },
            )
          }
          if (textures && textures.length) {
            for (const texture of textures) {
              const textureResource = _.find(
                _.flatMap(allResources, (value) => value),
                (resource) => {
                  return resource.prefix === texture.prefix
                },
              )
              if (textureResource) {
                await dispatch(
                  'creative/template/updateResource',
                  {
                    ...textureResource,
                    file_path: texture.file,
                  },
                  {
                    root: true,
                  },
                )
              }
            }
          }
          await dispatch('creative/template/saveTemplate', undefined, {
            root: true,
          })
        }
        return
      } catch (e) {
        console.error('convertCocosModelByQuery', e)
      }
    },
  },
}

export default ModelOrder
